import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, InputAdornment, IconButton
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import transporteService, { Transporte } from '../../services/transporteService';
import imputableService from '../../services/imputableService';
import LocalidadSearch from '../../components/LocalidadSearch';
import ImputableSearch from '../../components/ImputableSearch';
import type { Imputable } from '../../services/imputableService';

interface LocalidadSelect {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

const defaultForm: Transporte = {
  nroExpreso: 0,
  nombreExpreso: '',
  formaEntrega: null,
  observaciones: null,
  direccion: null,
  telefono1: null,
  telefono2: null,
  fax: null,
  codigoCiudad: null,
  nroCuenta: 0,
  cuit: null,
  lunes: null,
  martes: null,
  miercoles: null,
  jueves: null,
  viernes: null,
  sabado: null,
  zonas: null,
  costo: null,
  sincronizar: null
};

const formasEntrega = ['Envio', 'Retiro'];

const TransporteForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroExpreso } = useParams();
  const [formData, setFormData] = useState<Transporte>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [localidadSearchOpen, setLocalidadSearchOpen] = useState(false);
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState<LocalidadSelect | null>(null);
  const [imputableSeleccionado, setImputableSeleccionado] = useState<Imputable | null>(null);

  const loadTransporte = useCallback(async () => {
    if (!nroExpreso || nroExpreso === 'nuevo') return;
    try {
      setLoading(true);
      const transporte = await transporteService.getTransporte(nroExpreso);
      const t = transporte as Record<string, unknown>;
      const num = (v: unknown): number => (typeof v === 'number' && !isNaN(v) ? v : typeof v === 'string' ? Number(v) || 0 : 0);
      const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
      const strOrNull = (v: unknown): string | null => (v == null || v === '' ? null : str(v));
      const numOrNull = (v: unknown): number | null => (v == null || v === '' ? null : (typeof v === 'number' ? v : typeof v === 'string' ? Number(v) || null : null));
      const normalized: Transporte = {
        nroExpreso: num(t.nroExpreso ?? t.NroExpreso) || 0,
        nombreExpreso: str(t.nombreExpreso ?? t.NombreExpreso) || '',
        observaciones: strOrNull(t.observaciones ?? t.Observaciones),
        direccion: strOrNull(t.direccion ?? t.Direccion),
        telefono1: strOrNull(t.telefono1 ?? t.Telefono1),
        telefono2: strOrNull(t.telefono2 ?? t.Telefono2),
        fax: strOrNull(t.fax ?? t.Fax),
        codigoCiudad: numOrNull(t.codigoCiudad ?? t.CodigoCiudad),
        nroCuenta: num(t.nroCuenta ?? t.NroCuenta) || 0,
        cuit: strOrNull(t.cuit ?? t.Cuit),
        lunes: numOrNull(t.lunes ?? t.Lunes),
        martes: numOrNull(t.martes ?? t.Martes),
        miercoles: numOrNull(t.miercoles ?? t.Miercoles),
        jueves: numOrNull(t.jueves ?? t.Jueves),
        viernes: numOrNull(t.viernes ?? t.Viernes),
        sabado: numOrNull(t.sabado ?? t.Sabado),
        zonas: strOrNull(t.zonas ?? t.Zonas),
        costo: numOrNull(t.costo ?? t.Costo),
        formaEntrega: (() => {
          const v = t.formaEntrega ?? t.FormaEntrega;
          if (v == null || v === '') return null;
          const s = typeof v === 'string' ? v.trim() : String(v).trim();
          if (!s) return null;
          const match = formasEntrega.find(f => f.toLowerCase() === s.toLowerCase());
          return match ?? s;
        })(),
        sincronizar: numOrNull(t.sincronizar ?? t.Sincronizar)
      };
      setFormData(normalized);
      if (normalized.codigoCiudad) {
        setLocalidadSeleccionada({ codigoCiudad: normalized.codigoCiudad });
      }
      if (normalized.nroCuenta) {
        try {
          const imp = await imputableService.getImputable(normalized.nroCuenta);
          if (imp) {
            setImputableSeleccionado({ nroCuenta: imp.nroCuenta, nomCuenta: imp.nomCuenta ?? imp.descripcioncta ?? '' });
          } else {
            setImputableSeleccionado({ nroCuenta: normalized.nroCuenta, nomCuenta: '' });
          }
        } catch {
          setImputableSeleccionado({ nroCuenta: normalized.nroCuenta, nomCuenta: '' });
        }
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar el transporte', { variant: 'error' });
      navigate('/configuraciones/transportes');
    } finally {
      setLoading(false);
    }
  }, [nroExpreso, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroExpreso && nroExpreso !== 'nuevo') loadTransporte();
  }, [nroExpreso, loadTransporte]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    if (name === 'nroExpreso') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else if (name === 'costo' || name === 'nroCuenta') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value || null }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreExpreso?.trim()) {
        enqueueSnackbar('El nombre del transporte es requerido', { variant: 'warning' });
        return;
      }

      const nroCta = formData.nroCuenta ?? 0;
      if (nroCta <= 0) {
        enqueueSnackbar('El Nro. de Cuenta (Imputable) es requerido', { variant: 'warning' });
        return;
      }

      const payload: Transporte = {
        ...formData,
        nroCuenta: nroCta,
        observaciones: formData.observaciones || null,
        direccion: formData.direccion || null,
        telefono1: formData.telefono1 || null,
        telefono2: formData.telefono2 || null,
        fax: formData.fax || null,
        codigoCiudad: formData.codigoCiudad || null,
        cuit: formData.cuit || null,
        zonas: formData.zonas || null,
        formaEntrega: formData.formaEntrega || null
      };

      if (nroExpreso && nroExpreso !== 'nuevo') {
        await transporteService.updateTransporte(nroExpreso, payload);
      } else {
        await transporteService.createTransporte(payload);
      }

      enqueueSnackbar('Transporte guardado exitosamente', { variant: 'success' });
      navigate('/configuraciones/transportes');
    } catch (error: any) {
      console.error('Error al guardar transporte:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el transporte', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLocalidadSelect = (loc: { codigoCiudad: number; nombreCiudad?: string; nombreProvincia?: string }) => {
    setLocalidadSeleccionada(loc);
    setFormData(prev => ({ ...prev, codigoCiudad: loc.codigoCiudad }));
    setLocalidadSearchOpen(false);
  };

  const handleImputableSelect = (imp: Imputable) => {
    setImputableSeleccionado(imp);
    setFormData(prev => ({ ...prev, nroCuenta: imp.nroCuenta }));
    setImputableSearchOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroExpreso && nroExpreso !== 'nuevo' ? 'Editar Transporte' : 'Nuevo Transporte'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Datos de Contacto" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={2}>
                  <TextField 
                    name="nroExpreso" 
                    label="N° Expreso" 
                    type="number"
                    value={formData.nroExpreso || ''} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nroExpreso && nroExpreso !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={10}>
                  <TextField 
                    name="nombreExpreso" 
                    label="Nombre Expreso" 
                    value={formData.nombreExpreso || formData.nombre || ''} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="cuit" 
                    label="CUIT" 
                    value={formData.cuit || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="nroCuenta"
                    label="Nro. Cuenta"
                    value={imputableSeleccionado ? `${imputableSeleccionado.nroCuenta} - ${imputableSeleccionado.nomCuenta?.trim() || ''}` : (formData.nroCuenta ? String(formData.nroCuenta) : '')}
                    fullWidth
                    required
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setImputableSearchOpen(true)} edge="end" size="small" title="Buscar cuenta">
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>Forma de Entrega</InputLabel>
                    <Select
                      name="formaEntrega"
                      value={formData.formaEntrega || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, formaEntrega: e.target.value }))}
                      label="Forma de Entrega"
                    >
                      <MenuItem value="">Ninguna</MenuItem>
                      {formasEntrega.map(forma => (
                        <MenuItem key={forma} value={forma}>{forma}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.sincronizar}
                        onChange={(e) => setFormData(prev => ({ ...prev, sincronizar: e.target.checked ? 1 : 0 }))}
                      />
                    }
                    label="Sincronizar con el ecommerce"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="observaciones" 
                    label="Observaciones" 
                    value={formData.observaciones || ''} 
                    onChange={handleChange} 
                    fullWidth 
                    multiline 
                    rows={2}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Datos de contacto del transporte
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="direccion" 
                    label="Dirección" 
                    value={formData.direccion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="codigoCiudad"
                    label="Código Ciudad / Localidad"
                    value={localidadSeleccionada 
                      ? `${localidadSeleccionada.codigoCiudad}${localidadSeleccionada.nombreCiudad ? ' - ' + localidadSeleccionada.nombreCiudad : ''}${localidadSeleccionada.nombreProvincia ? ' - ' + localidadSeleccionada.nombreProvincia : ''}` 
                      : (formData.codigoCiudad ? String(formData.codigoCiudad) : '')}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setLocalidadSearchOpen(true)} edge="end" size="small" title="Buscar localidad">
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="telefono1" 
                    label="Teléfono 1" 
                    value={formData.telefono1 || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="telefono2" 
                    label="Teléfono 2" 
                    value={formData.telefono2 || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="fax" 
                    label="Fax" 
                    value={formData.fax || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="zonas" 
                    label="Zonas" 
                    value={formData.zonas || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="costo" 
                    label="Costo" 
                    type="number"
                    value={formData.costo ?? ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: 0.01, min: 0 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Días de atención
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    {(['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'] as const).map(dia => (
                      <FormControlLabel
                        key={dia}
                        control={
                          <Checkbox
                            checked={!!(formData[dia] ?? 0)}
                            onChange={(e) => setFormData(prev => ({ ...prev, [dia]: e.target.checked ? 1 : 0 }))}
                          />
                        }
                        label={dia.charAt(0).toUpperCase() + dia.slice(1)}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/transportes')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>

          <LocalidadSearch
            open={localidadSearchOpen}
            onClose={() => setLocalidadSearchOpen(false)}
            onSelect={handleLocalidadSelect}
          />
          <ImputableSearch
            open={imputableSearchOpen}
            onClose={() => setImputableSearchOpen(false)}
            onSelect={handleImputableSelect}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TransporteForm;
